import React, { useCallback, useState, useEffect, useMemo } from 'react';
import { KiteButton, KiteInput, KiteAlert, KiteModal } from '@kite/react-kite';
import { ITcoLocations, TcoUnderlayProduct } from 'types/TcoProducts';
import { useParams } from 'react-router-dom';
import { useQueryData } from 'hooks';
import { useGetTcoUnderlayProducts } from 'hooks/apiHooks/tcoUnderlayProducts/useGetUnderlayProducts';
import { checkFormErrors } from 'utils';
import UnderlaySection, {
  UnderlayFormData,
} from '../../UnderlaySection/UnderlaySection';
import './ApplyUnderlayByLocationGroups.scss';

export interface IApplyUnderlayByLocationGroupsProps {
  onAddLocation: (location: ITcoLocations) => void;
  onEditLocation: (
    location: ITcoLocations,
    originalLocation: ITcoLocations
  ) => void;
  locationToEdit: ITcoLocations | null;
  onCancelEdit: () => void;
  term: string;
}

const MAX_UNDERLAYS = 2;

const ApplyUnderlayByLocationGroups = ({
  onAddLocation,
  onEditLocation,
  locationToEdit,
  onCancelEdit,
  term,
}: IApplyUnderlayByLocationGroupsProps) => {
  const { estimateId = '' } = useParams();
  const { userId } = useQueryData();
  const {
    data: rawUnderlayProducts = [],
    isLoading: isLoadingUnderlayProducts,
  } = useGetTcoUnderlayProducts();

  const underlayProducts = useMemo<TcoUnderlayProduct[]>(() => {
    return rawUnderlayProducts.map((product) => ({
      ...product,
      description: product.description || '',
      prices: (product as any).prices || [],
    }));
  }, [rawUnderlayProducts]);

  const [showModal, setShowModal] = useState(false);
  const [locationName, setLocationName] = useState('');
  const [noOfSites, setNoOfSites] = useState('');
  const [underlayForms, setUnderlayForms] = useState<UnderlayFormData[]>([]);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [globalError, setGlobalError] = useState(false);

  const toggleModal = useCallback(() => {
    setShowModal((prev) => !prev);
    if (!showModal) {
      setLocationName('');
      setNoOfSites('');
      setUnderlayForms([]);
      setFormErrors({});
      setGlobalError(false);
    } else {
      setFormErrors({});
      setGlobalError(false);
      if (locationToEdit) {
        onCancelEdit();
      }
    }
  }, [showModal, locationToEdit, onCancelEdit]);

  useEffect(() => {
    if (locationToEdit) {
      setLocationName(locationToEdit.name);
      setNoOfSites(locationToEdit.noOfSites.toString());

      if (locationToEdit.underlaySelections?.length > 0) {
        const formData = locationToEdit.underlaySelections.map((selection) => ({
          id: selection.id,
          underlayId: selection.underlayId,
          spectrumPrice: selection.spectrumPrice.toString(),
          diyPrice: selection.diyPrice.toString(),
        }));
        setUnderlayForms(formData);
      } else {
        setUnderlayForms([]);
      }

      setFormErrors({});
      setGlobalError(false);
      setShowModal(true);
    } else {
      setLocationName('');
      setNoOfSites('');
      setUnderlayForms([]);
    }
  }, [locationToEdit]);

  const handleNoOfSitesChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      if (value === '' || /^[0-9]+$/.test(value)) {
        setNoOfSites(value);
        setFormErrors((prev) => ({ ...prev, noOfSites: '' }));
        setGlobalError(false);
      }
    },
    []
  );

  const handleNoOfSitesKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === '.' || e.key.toLowerCase() === 'e') {
        e.preventDefault();
      }
    },
    []
  );

  const handleAddUnderlay = useCallback(() => {
    if (underlayForms.length < MAX_UNDERLAYS) {
      setUnderlayForms((prev) => [
        ...prev,
        {
          underlayId: '',
          spectrumPrice: '',
          diyPrice: '',
        },
      ]);
    }
  }, [underlayForms]);

  const handleDeleteUnderlay = useCallback((index: number) => {
    setUnderlayForms((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleUnderlayChange = useCallback(
    (index: number, field: keyof UnderlayFormData, value: string) => {
      setUnderlayForms((prev) => {
        const newUnderlayForms = [...prev];
        newUnderlayForms[index] = {
          ...newUnderlayForms[index],
          [field]: value,
        };
        return newUnderlayForms;
      });

      setFormErrors((prev) => ({
        ...prev,
        [`${field}_${index}`]: '',
      }));
      setGlobalError(false);
    },
    []
  );

  const validateForm = useCallback(() => {
    const { hasErrors: isLocationNameError, errorObject: locationNameErrors } =
      checkFormErrors({ locationName }, ['locationName']);

    const { hasErrors: initialNoOfSitesError, errorObject: noOfSitesErrors } =
      checkFormErrors({ noOfSites }, ['noOfSites']);

    const updatedNoOfSitesErrors = { ...noOfSitesErrors };
    let isNoOfSitesError = initialNoOfSitesError;

    if (!isNoOfSitesError && noOfSites) {
      const sitesCount = parseInt(noOfSites, 10);
      if (isNaN(sitesCount) || sitesCount <= 0) {
        updatedNoOfSitesErrors.noOfSites = 'Minimum of 1 required';
        isNoOfSitesError = true;
      }
    }

    let hasUnderlayErrors = false;
    const underlayErrors: Record<string, string> = {};

    if (underlayForms.length > 0) {
      underlayForms.forEach((form, index) => {
        const { hasErrors, errorObject } = checkFormErrors(
          form,
          ['underlayId', 'spectrumPrice', 'diyPrice'],
          { underlayId: 'select' }
        );

        if (hasErrors) {
          hasUnderlayErrors = true;
          Object.entries(errorObject).forEach(([field, error]) => {
            underlayErrors[`${field}_${index}`] = error;
          });
        }
      });
    }

    const hasErrors =
      isLocationNameError || isNoOfSitesError || hasUnderlayErrors;

    if (hasErrors) {
      setGlobalError(true);
      setFormErrors({
        ...locationNameErrors,
        ...updatedNoOfSitesErrors,
        ...underlayErrors,
      });
    }

    return !hasErrors;
  }, [locationName, noOfSites, underlayForms]);

  const handleAddLocation = useCallback(() => {
    if (!validateForm()) {
      return;
    }

    const formatPrice = (price: string): number => {
      const num = parseFloat(price);
      return isNaN(num) ? 0 : parseFloat(num.toFixed(2));
    };

    const sitesCount = noOfSites ? parseInt(noOfSites, 10) : 1;

    if (locationToEdit) {
      const updatedLocation: ITcoLocations = {
        id: locationToEdit.id,
        estimateId,
        name: locationName,
        noOfSites: sitesCount,
        underlaySelections: underlayForms.map((form) => ({
          id: form.id || '',
          locationId: locationToEdit.id,
          underlayId: form.underlayId || underlayProducts[0]?.id || '',
          term: term,
          spectrumPrice: formatPrice(form.spectrumPrice),
          diyPrice: formatPrice(form.diyPrice),
          updatedBy: userId,
          createdAt: '',
          updatedAt: '',
        })),
        updatedBy: userId,
        createdAt: locationToEdit.createdAt || '',
        updatedAt: locationToEdit.updatedAt || '',
      };

      onEditLocation(updatedLocation, locationToEdit);
    } else {
      const newLocation: ITcoLocations = {
        id: '',
        name: locationName,
        noOfSites: sitesCount,
        estimateId,
        updatedBy: userId,
        underlaySelections: underlayForms.map((form) => ({
          id: '',
          locationId: '',
          underlayId: form.underlayId || underlayProducts[0]?.id || '',
          term: term,
          spectrumPrice: formatPrice(form.spectrumPrice),
          diyPrice: formatPrice(form.diyPrice),
          updatedBy: userId,
          createdAt: '',
          updatedAt: '',
        })),
        createdAt: '',
        updatedAt: '',
      };

      onAddLocation(newLocation);
    }

    toggleModal();
  }, [
    locationName,
    noOfSites,
    underlayForms,
    locationToEdit,
    onEditLocation,
    onAddLocation,
    userId,
    estimateId,
    toggleModal,
    validateForm,
    underlayProducts,
    term,
  ]);

  return (
    <>
      <div className="locations__button-container">
        <KiteButton type="outline" onClick={toggleModal} leftIcon="plus-circle">
          Add Location Groups
        </KiteButton>
      </div>

      <KiteModal
        title={locationToEdit ? 'Edit Location Groups' : 'Add Location Groups'}
        canShow={showModal}
        onHide={toggleModal}
        ctaCopy={locationToEdit ? 'Save' : 'Add'}
        ctaAction={handleAddLocation}
        secondaryCtaCopy="Cancel"
        secondaryCtaAction={toggleModal}
        className="location-modal-title"
        disableBackgroundClose={true}
      >
        <div className="location-modal-content">
          {globalError && (
            <KiteAlert
              description="Please fill out the missing fields"
              type="alert"
              className="location-modal__alert"
            />
          )}

          <div className="location-form-row">
            <div className="location-name-container">
              <label htmlFor="locationNameInput" className="kite-label">
                Location Group Name
              </label>
              <KiteInput
                id="locationNameInput"
                name="locationName"
                value={locationName}
                onChange={(e) => {
                  setLocationName(e.target.value);
                  setFormErrors((prev) => ({ ...prev, locationName: '' }));
                  setGlobalError(false);
                }}
                placeholder="Enter Location Group Name"
                maxWidth="none"
                errorMessage={formErrors.locationName}
              />
            </div>
            <div className="no-of-sites-container">
              <label htmlFor="noOfSitesInput" className="kite-label">
                Number of Sites
              </label>
              <KiteInput
                id="noOfSitesInput"
                name="noOfSites"
                value={noOfSites}
                onChange={handleNoOfSitesChange}
                onKeyDown={handleNoOfSitesKeyDown}
                placeholder="Enter Number"
                type="number"
                min="1"
                maxWidth="none"
                errorMessage={formErrors.noOfSites}
              />
            </div>
          </div>

          <UnderlaySection
            underlayForms={underlayForms}
            formErrors={formErrors}
            underlayProducts={underlayProducts}
            onAddUnderlay={handleAddUnderlay}
            onDeleteUnderlay={handleDeleteUnderlay}
            onUnderlayChange={handleUnderlayChange}
            maxUnderlays={MAX_UNDERLAYS}
            globalError={globalError}
            isLoading={isLoadingUnderlayProducts}
            term={term}
            noOfSites={noOfSites}
          />
        </div>
      </KiteModal>
    </>
  );
};

export default ApplyUnderlayByLocationGroups;
